// ABOUTME: The results card for the relation Inkoopadvies page — SKUs to reorder, total advised units
// ABOUTME: and purchase value, plus a sentence echoing the active analysis period and forecast horizon.
import { Box, ButtonBase, useTheme } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import EMAdviceCoverage from "../../../components/global/ModalsPopover/explainers/purchaseadvice/EMAdviceCoverage";
import SHQuickAction from "../../../components/theme/Modal/SHQuickAction";
import ShLabel from "../../../components/theme/label/ShLabel";
import SidepanelLoader from "../../../components/theme/sidepanel2/SidepanelLoader";
import Text from "../../../components/theme/text/Text";
import { formatNumber, formatPrice } from "../../../utils/helpers";
import { useGetStockForecastQuery } from "../../../newapi/warehouse/productSlice";
import { tokens } from "../../../theme";
import Heading from "../../../components/theme/text/Heading";
import StatCard from "../../../components/theme/cards/StatCard";
import StatCardRow from "../../../components/theme/cards/StatCardRow";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import LayersIcon from "@mui/icons-material/Layers";
import EuroIcon from "@mui/icons-material/Euro";
import CalculateIcon from "@mui/icons-material/Calculate";

const SpInboundDetails = SidepanelLoader(() => import("../../../components/global/Sidepanels/SpInboundDetails/SpInboundDetails"));

const PurchaseAdviceCards = ({ params }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const navigate = useNavigate();

    const [spLoading, setSpLoading] = useState(false);
    const [inboundIsExtended, setInboundIsExtended] = useState(false);
    const [inboundId, setInboundId] = useState();
    const inboundIdRef = useRef(inboundId);

    const getInboundDetails = (id) => {
        if (!id) return;

        const newInboundId = parseInt(id);
        // Only update state if the inboundId has actually changed
        if (inboundIdRef.current !== newInboundId) {
            if (!spLoading) {
                setSpLoading(true);
            }
            setInboundId(newInboundId);
            inboundIdRef.current = newInboundId;
        }
        setInboundIsExtended(true);
    };

    // Same endpoint as the grid, sized to one row: we only need the summary block, not the page.
    const { data, isFetching } = useGetStockForecastQuery({
        args: { page: 1, size: 1, body: { ...params } },
    });

    const summary = data?.summary ?? {};
    // Products an open inbound only partly answers: an order is on its way, but one still has to be
    // placed on top of it. Fully covered products are not listed — there is nothing left to buy for
    // them. The whole panel disappears when nothing is partly covered.
    const covered = summary.coveredProducts ?? [];
    const coveredCount = summary.productsPartlyCovered ?? 0;
    const num = (v) => formatNumber(i18n, v ?? 0);
    const price = (v) => formatPrice(i18n, v ?? 0);
    // Until the backend returns summary, fall back to the pager's total for "SKUs to reorder".
    const skusToReorder = summary.productsNeedingRestock ?? data?.totalItems;

    // Same filled card grid as the invoice totals panel, so the two overview pages read alike.
    const tiles = [
        { key: "skus", label: t("skus_to_reorder"), value: num(skusToReorder), icon: <Inventory2Icon className="logo"/>, color: "blue" },
        { key: "units", label: t("total_advised_units"), value: num(summary.totalRecommendedQuantity), icon: <LayersIcon className="logo"/>, color: "green" },
        { key: "value", label: t("total_purchase_value"), value: price(summary.totalRecommendedValue), icon: <EuroIcon className="logo"/>, color: "orange" },
        { key: "avg", label: t("avg_purchase_value_per_sku"), value: price(summary.averageRecommendedValuePerSku), icon: <CalculateIcon className="logo"/>, color: "purple" },
    ];

    const tileSx = {
        flex: 1,
        minWidth: 160,
        background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[50],
        border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[200]}`,
        borderRadius: 3,
        p: 2,
        px: 2.5,
        display: "flex",
        flexDirection: "column",
        justifyContent:"space-between",
        gap: 1,
    };

    return (
        <Box
            sx={{
                flex: 3,
                minWidth: 280,
                display: "flex",
                gap: 2.5,
                p: 3,
                borderRadius: 5,
                background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[200]}`,
                boxShadow: theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
            }}
        >
            <Box
                sx={{
                    display:'flex',
                    flexDirection:'column',

                    flex:3,
                }}
            >
                <Text bold sx={{ fontSize: 16 }}>{t("result_of_selection")}</Text>
                {/* Restates the two calculation windows in prose, bolding the active values. */}
                <Text light sx={{ fontSize: 14 }}>
                    {params.basis === "stocklevel" ? t("advice_calculated_stock_level") :
                    <Trans
                        i18nKey="advice_calculated"
                        values={{
                            months: params.monthsBack !== 1 ? `${params.monthsBack} ${t("months")}` : `${params.monthsBack} ${t("month")}`,
                            days: params.daysInFuture !== 1 ? `${params.daysInFuture} ${t("days")}` : `${params.daysInFuture} ${t("day")}`,
                        }}
                        components={{ b: <Text bold sx={{ fontSize: 14 }} /> }}
                    />
                    }
                </Text>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2,flex:1, mt:2.5, }}>
                    <StatCardRow columns={2} sx={{ flex: 2, minWidth: 320, gridAutoRows: "minmax(0, 1fr)" }}>
                        {tiles.map((tile) => (
                            <StatCard
                                filled
                                key={tile.key}
                                label={tile.label}
                                value={tile.value}
                                icon={tile.icon}
                                color={tile.color}
                                isLoading={isFetching}
                            />
                        ))}
                    </StatCardRow>

                    {covered.length > 0 && (
                        <Box sx={{ ...tileSx, flex: 2, minWidth: 280, justifyContent: "flex-start", gap: 0 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
                                <Box sx={{ display: "flex", flexDirection: "column" }}>
                                    <Text sx={{ fontSize: 14 }}>{t("already_purchased")}</Text>
                                    {/* The panel only holds a handful of rows while the set behind it can run
                                        into the hundreds. Under the title, the count needs no qualifier. */}
                                    {!isFetching && coveredCount > covered.length  && (
                                        <Box>
                                            <Text noBreak>
                                                {covered.length}&nbsp;
                                            </Text>
                                            <Text light noBreak>
                                                {t("shown")}
                                            </Text>
                                        </Box>
                                    )}
                                </Box>
                                {/* Products with an inbound on the way. Units only mean something per row —
                                    summing them across products gives a number nothing can be done with. */}
                                <Heading >{num(coveredCount)}</Heading>
                            </Box>

                            <Box
                                sx={{
                                    flex: 1,
                                    mt: 1.5,
                                    p: 1.5,
                                    borderRadius: 3,
                                    // Double background: primary[100] is semi-transparent, so it goes on as
                                    // the gradient layer over a solid white base. That keeps the tint the
                                    // same everywhere instead of picking up whatever sits behind the card.
                                    backgroundColor: theme.palette.mode === "dark" ? colors.blue[100] : "#fff",
                                    backgroundImage: theme.palette.mode === "dark" ? null : `linear-gradient(${colors.primary[100]}, ${colors.primary[100]})`,
                                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.primary[400]}`,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                }}
                            >
                                {/* Column widths are repeated on the rows below so the two line up. */}
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                    <Text light noBreak sx={{ fontSize: 12, flex: 1 }}>{t("inbound")}</Text>
                                    <Text light noBreak sx={{ fontSize: 12, flex: 1 }}>{t("sku")}</Text>
                                    {/* Not the inbound's state — whether what is on its way covers the
                                        advice for this product, or only part of it. */}
                                    <Box sx={{ width: 92, display: "flex", alignItems: "center", gap: 0.5, }}>
                                        <Text light noBreak sx={{ fontSize: 12 }}>{t("advice_coverage")}</Text>
                                        <EMAdviceCoverage />
                                    </Box>
                                    <Text light noBreak sx={{ fontSize: 12, width: 56, textAlign: "right" }}>{t("items")}</Text>
                                </Box>

                                {!isFetching && covered.map((product) => (
                                    // The whole row opens the covering inbound in the sidepanel.
                                    <Box
                                        key={product.productId}
                                        onClick={() => getInboundDetails(product.inbounds?.[0]?.inboundId)}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.5,
                                            cursor: "pointer",
                                            transition: "250ms",
                                            "&:hover": {
                                                filter: theme.palette.mode === "dark" ? "brightness(1.15)" : "brightness(0.53)",
                                                transition: "250ms",
                                            },
                                        }}
                                    >
                                        {/* One inbound shows its barcode outright. Several would not fit
                                            the row, so they collapse to a count whose hover lists them
                                            individually, each opening its own inbound. */}
                                        <Box sx={{ flex: 1, display:"flex",alignItems:'center', }}>

                                            {(product.inbounds ?? []).length === 1 && (
                                                <Text
                                                    semibold
                                                    noBreak
                                                    variant={theme.palette.mode === "dark" ? "blue" : "primary"}
                                                    sx={{ fontSize: 13 }}
                                                >
                                                    {product.inbounds[0].barcode}
                                                </Text>
                                            )}

                                            {(product.inbounds ?? []).length > 1 && (
                                                <SHQuickAction
                                                    interactive
                                                    component={
                                                        <Box sx={{ minWidth: 280, display: "flex", flexDirection: "column", gap: 1.5 }}>
                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                                <Text light noBreak>{t("inbound")}</Text>
                                                                <Text light noBreak sx={{ ml: "auto" }}>{t("items")}</Text>
                                                            </Box>

                                                            {product.inbounds.map((inbound) => (
                                                                <Box
                                                                    key={inbound.inboundId}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        getInboundDetails(inbound.inboundId);
                                                                    }}
                                                                    sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                                                                >
                                                                    <Text
                                                                        clickable
                                                                        semibold
                                                                        noBreak
                                                                        variant={theme.palette.mode === "dark" ? "blue" : "primary"}
                                                                    >
                                                                        {inbound.barcode}
                                                                    </Text>
                                                                    <Text noBreak sx={{ ml: "auto" }}>
                                                                        {formatNumber(i18n, inbound.incomingQuantity)}
                                                                    </Text>
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    }
                                                >
                                                    {({ anchorEl, onMouseEnter, onMouseLeave, onTouchStart }) => (
                                                        <Box
                                                            aria-owns={anchorEl ? "simple-popover" : undefined}
                                                            aria-haspopup="true"
                                                            onMouseEnter={onMouseEnter}
                                                            onMouseLeave={onMouseLeave}
                                                            onTouchStart={onTouchStart}
                                                            sx={{ display: "inline-flex" }}
                                                        >
                                                            <Text
                                                                clickable
                                                                semibold
                                                                noBreak
                                                                variant={theme.palette.mode === "dark" ? "blue" : "primary"}
                                                                sx={{ fontSize: 13 }}
                                                            >
                                                                {product.inbounds.length} {t("inbounds")}
                                                            </Text>
                                                        </Box>
                                                    )}
                                                </SHQuickAction>
                                            )}
                                        </Box>
                                        <Text noBreak sx={{ fontSize: 13, flex: 1 }}>{product.sku}</Text>
                                        {/* Every row here is partly covered — the badge says which of the
                                            two coverage states the explainer describes applies. */}
                                        <Box sx={{ width: 92 }}>
                                            <ShLabel clickable fitted size={24} palette="normal" variant="orange">
                                                {t("partly_covered")}
                                            </ShLabel>
                                        </Box>
                                        <Text semibold sx={{ fontSize: 13, width: 56, textAlign: "right" }}>
                                            {formatNumber(i18n, product.incomingQuantity)}
                                        </Text>
                                    </Box>
                                ))}
                            </Box>

                            <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
                                <ButtonBase
                                    onClick={() => navigate("/relation/inbound")}
                                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                                >
                                    <Text semibold variant={theme.palette.mode === "dark" ? "blue" : "primary"} sx={{ fontSize: 13 }}>
                                        {t("all_inbounds")}
                                    </Text>
                                    <ArrowForwardIcon
                                        sx={{
                                            fontSize: 16,
                                            fill: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                                        }}
                                    />
                                </ButtonBase>

                            </Box>
                        </Box>
                    )}
                </Box>


            </Box>

            {spLoading &&
                <SpInboundDetails
                    isExtended={inboundIsExtended}
                    setIsExtended={setInboundIsExtended}
                    inboundId={inboundId}
                />
            }
        </Box>
    );
};

export default PurchaseAdviceCards;
